import type { QcsSyncResourceStore, QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';
import { List as ImmutableList, Map as ImmutableMap, Record, type RecordOf } from 'immutable';
import type { List, Map } from 'immutable';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import { chartOfAccountsTypes } from '../chart-of-accounts';
import PaymentMethodType from '../fmcore-types/PaymentMethodTypes';
import { tagsTypes } from '../tags';

export const TRANSACTIONS_REDUCER_KEY = 'transactionStore';

export interface TransactionStoreProps extends QcsSyncResourceStoreProps<TransactionProps> {
  dateOnAfter?: string;
  dateOnBefore?: string;
  earliestDateOn?: string;

  lastReductions?: Map<string, List<Map<string, any>>> | null; // { lastReductions: [{ priorValue, newValue, upsertId }]}
}

export type TransactionStore = QcsSyncResourceStore<TransactionProps, TransactionStoreProps>;
export const mkTransactionStore = Record<TransactionStoreProps>({
  resourcesById: ImmutableMap(),

  resourceName: 'TRANSACTIONS',
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  error: undefined,

  dateOnAfter: undefined,
  dateOnBefore: undefined,
  earliestDateOn: undefined,

  lastReductions: null,
});

export type LinkedItemProps = {
  id?: string | null;
  clientId?: string | null;
  childId?: string | null;
  childClientId?: string | null;
};
export type LinkedItem = RecordOf<LinkedItemProps>;
export const mkLinkedItem = Record<LinkedItemProps>({
  id: null,
  clientId: null,
  childId: null,
  childClientId: null,
});

export const SplitItemType = Object.freeze({
  NORMAL: 'NORMAL',

  INVOICE_BILLABLE_EXPENSE: 'INVOICE_BILLABLE_EXPENSE',
  INVOICE_BILLABLE_ITEM: 'INVOICE_BILLABLE_ITEM',
  INVOICE_BILLABLE_TIME: 'INVOICE_BILLABLE_TIME',
  INVOICE_BLANK_LINE: 'INVOICE_BLANK_LINE',
  INVOICE_BUNDLE: 'INVOICE_BUNDLE',
  INVOICE_CREDIT: 'INVOICE_CREDIT',
  INVOICE_DEPOSIT: 'INVOICE_DEPOSIT',
  INVOICE_DISCOUNT: 'INVOICE_DISCOUNT',
  INVOICE_ITEM_DISCOUNT: 'INVOICE_ITEM_DISCOUNT',
  INVOICE_ITEM_PRICE_RULE: 'INVOICE_ITEM_PRICE_RULE',
  INVOICE_ITEM_SUBTOTAL: 'INVOICE_ITEM_SUBTOTAL',
  INVOICE_ITEM_SURCHARGE: 'INVOICE_ITEM_SURCHARGE',
  INVOICE_ITEM_TAX: 'INVOICE_ITEM_TAX',
  INVOICE_SHIPPING: 'INVOICE_SHIPPING',
  INVOICE_SHIPPING_TAX: 'INVOICE_SHIPPING_TAX',
  INVOICE_SUBTOTAL: 'INVOICE_SUBTOTAL',
  INVOICE_TAX: 'INVOICE_TAX',
  INVOICE_TAX_PRE_DISCOUNT: 'INVOICE_TAX_PRE_DISCOUNT',

  LOAN_INTEREST: 'LOAN_INTEREST',
  LOAN_PRINCIPAL: 'LOAN_PRINCIPAL',
});

export type SplitItemProps = {
  id?: string | null;
  clientId?: string | null;
  parentItemId?: string | null;
  parentItemClientId?: string | null;
  type?: (typeof SplitItemType)[keyof typeof SplitItemType] | null;
  serviceOn?: string | null;
  itemId?: string | null;
  memo?: string | null;
  tags?: List<tagsTypes.Tag> | Array<tagsTypes.TagProps> | null;
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null;
  amount: number;
  units?: number | null;
  rate?: number | null;
  isRatePercentage?: boolean | null;
  businessId?: string | null;
  customerId?: string | null;
  projectId?: string | null;
  isBillable?: boolean | null;
  isTaxable?: boolean | null;
  transfer?: { clientId?: string | null; id?: string | null } | null;
  linkedItem?: LinkedItem | LinkedItemProps | null;
  isExcludedFromF2S?: boolean | null;
  isExcludedFromReports?: boolean | null;
};

export type SplitProps = {
  items?: List<RecordOf<SplitItemProps>> | Array<SplitItemProps> | null;
};

export const BaseSplitItem = ({ tags, coa, ...defaultValues }: Partial<SplitItemProps>) =>
  class extends Record<SplitItemProps>({
    id: defaultValues.id || null,
    clientId: null,

    parentItemId: undefined,
    parentItemClientId: undefined,

    type: SplitItemType.NORMAL,

    serviceOn: undefined,
    itemId: undefined,
    memo: undefined,

    coa: chartOfAccountsTypes.mkChartOfAccount({
      id: '0',
      type: 'UNCATEGORIZED',
    }),
    amount: 0.0,

    units: undefined,
    rate: undefined,
    isRatePercentage: false,

    businessId: undefined,
    customerId: undefined,
    projectId: undefined,
    tags: ImmutableList([]),

    isBillable: false,
    isTaxable: false,

    transfer: undefined,
    linkedItem: undefined,

    // These cannot be set by default, cause on QCS, txn should override unless user specifically sets. Currently,
    // client doesn't yet support setting these at the split-level in the UI.
    isExcludedFromF2S: undefined,
    isExcludedFromReports: undefined,

    ...defaultValues,
  }) {};

export const BaseSplit = (defaultValues: Partial<SplitProps>) =>
  class extends Record<SplitProps>({
    items: null,

    ...defaultValues,
  }) {};

export class Split extends BaseSplit({}) {}

export class SplitItem extends BaseSplitItem({}) {}

export const mkSplit = (split: SplitProps | { items: SplitItemProps[] }) =>
  new Split({
    items: mkSplitItems(split.items || ImmutableList()),
  });

export const mkSplitItem = (item: Partial<SplitItemProps> | RecordOf<SplitItemProps>) =>
  new SplitItem({
    ...(('toJS' in item ? item.toJS() : item) as SplitItemProps),
    tags: item.tags ? ImmutableList(item.tags.map((tag) => tagsTypes.mkTag(tag))) : undefined,
    coa: item.coa ? chartOfAccountsTypes.mkChartOfAccount({ id: item.coa.id, type: item.coa.type }) : null,
    linkedItem: item.linkedItem ? mkLinkedItem(mkLinkedItem(item.linkedItem)) : undefined,
  });

export function mkSplitItems(items: List<RecordOf<SplitItemProps>> | Array<SplitItemProps>) {
  return ImmutableList(items.map((item) => mkSplitItem(item)));
}

export function mkSplitCopy(split: RecordOf<SplitProps>) {
  if (split?.items) {
    const newSplit = split.set(
      'items',
      split.items.map((item) =>
        'set' in item
          ? item.set('id', undefined).set('clientId', uuidv4())
          : mkSplitItem({ ...item, id: undefined, clientId: uuidv4() } as SplitItemProps),
      ),
    );
    return mkSplit(newSplit.toJS() as SplitProps);
  }
  return split;
}

export type AllocationProps = {
  txnId?: string | null;
  amount?: number | null;
};

export const BaseAllocation = (defaultValues?: Partial<AllocationProps> | null) =>
  class extends Record<AllocationProps>({
    txnId: null,
    amount: null,

    ...defaultValues,
  }) {};
export class Allocation extends BaseAllocation({}) {}
export const mkAllocation = (allocation: Partial<AllocationProps> = {}) => new Allocation(allocation);

export type QcsColor = 'RED' | 'ORANGE' | 'YELLOW' | 'GREEN' | 'BLUE' | 'VIOLET' | 'BLACK' | 'BROWN' | 'NONE';

export enum TransactionSource {
  UserEntered = 'USER_ENTERED',
  QcsRefresh = 'QCS_REFRESH',
  QcsSync = 'QCS_SYNC',
  QcsBatchRefresh = 'QCS_BATCH_REFRESH',
  ClientAggregated = 'CLIENT_AGGREGATED',
  ClientAggregatedBankPending = 'CLIENT_AGGREGATED_BANK_PENDING',
  QcsRefreshBankPending = 'QCS_REFRESH_BANK_PENDING',
  QcsBatchRefreshBankPending = 'QCS_BATCH_REFRESH_BANK_PENDING',
  RealTime = 'REAL_TIME',
  UserOwnedBankPending = 'USER_OWNED_BANK_PENDING',
  UserOwnedRealTimeBankPending = 'USER_OWNED_REAL_TIME_BANK_PENDING',
  ScheduledTransactionPending = 'SCHEDULED_TRANSACTION_PENDING',
  ScheduledTransactionUnkown = 'SCHEDULED_TRANSACTION_UNKNOWN',
  ScheduledTransaction = 'SCHEDULED_TRANSACTION',
  ScheduledTransactionSkipped = 'SCHEDULED_TRANSACTION_SKIPPED',
  Unknown = 'UNKNOWN',
}

export enum TransactionSubType {
  A2aTransfer = 'A2A_TRANSFER',
  A2aTransferShadowTxn = 'A2A_SHADOW_TXN',

  GoalStartingAmount = 'GOAL_STARTING_AMOUNT',
  GoalSpent = 'GOAL_SPENT',

  // business subtypes
  InvoiceCreditMemo = 'INVOICE_CREDIT_MEMO',
  InvoicePayment = 'INVOICE_PAYMENT',
  Invoice = 'INVOICE',
  InvoiceRefund = 'INVOICE_REFUND',
  SalesReceitp = 'SALES_RECEIPT',
  UnearnedIncome = 'UNEARNED_INCOME',

  Refund = 'REFUND',
  RefundShadowTxn = 'REFUND_SHADOW_TXN',
}

export type TransactionState = 'CLEARED' | 'PENDING' | 'RECONCILED';

export type TransactionType = 'INVESTMENT' | 'CASH_FLOW' | 'INVOICE';

export type CpData = {
  amount?: number;
  cpCategoryId?: string;
  id?: string;
  inferredCoa?: chartOfAccountsTypes.ChartOfAccount | chartOfAccountsTypes.ChartOfAccountProps;
  payee?: string;
  inferredPayee?: string;
  memo?: string;
  postedOn?: string;
  txOn?: string;
};

export type Attachment = { id: string };

export type TransactionProps = {
  id?: string | null;
  clientId?: string | null;
  memo?: string | null;
  amount: number;
  tags?: List<tagsTypes.Tag> | Array<tagsTypes.TagProps> | null;
  accountId?: string | null;
  postedOn: string;
  isBillable?: boolean | null;
  isExcludedFromReports?: boolean | null;
  isExcludedFromF2S?: boolean | null;
  userFlag?: QcsColor | null;
  userFlagNote?: string | null;
  payee?: string | null;
  knownCategoryId?: string | null;
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null;
  split?: RecordOf<SplitProps> | SplitProps | null;
  attachments?: Attachment[] | null;
  isReviewed?: boolean | null;
  state?: TransactionState;
  source?: TransactionSource;
  stModelId?: string | null;
  stDueOn?: string | null;
  paymentProvider?: string | null;
  paymentSentStatus?: string | null;
  paymentId?: string | null;
  createdAt?: string | null;
  allocations?: List<Allocation> | Array<AllocationProps> | null;
  modifiedAt?: string | null;
  isBill?: boolean;
  ebillId?: string | null;
  isSubscription?: boolean;
  subtype?: TransactionSubType | null;
  subtypeRelatedTxn?: { id?: string } | null; // deprecated - use linkedItem instead
  linkedItem?: LinkedItem | LinkedItemProps | null;
  type?: TransactionType | null;
  isDeleted?: boolean | null;
  transfer?: { clientId?: string | null; id?: string | null } | null;
  matchedTxn?: { id?: string } | null;
  investmentTxnType?: (typeof InvestmentTxnType)[keyof typeof InvestmentTxnType] | '' | null;
  units?: number | null;
  cpData?: CpData | null;
  localSecurityId?: string | null;
  securityId?: string | null; // deprecated - use localSecurityId instead
  securityIdType?: string | null; // deprecated - use localSecurityId instead
  securityType?: string | null; // deprecated - use localSecurityId instead
  symbol?: string | null;
  securityName?: string | null;
  costBasis?: number | null;
  commission?: number | null;
  splitNumerator?: number | null;
  splitDenominator?: number | null;
  // FIXME: This prop may not exist
  acquisitionOn?: string | null;
  // FIXME: This prop may not exist
  currency?: string | null;
  businessId?: string | null;
  matchState?: string | null;
  customerId?: string | null;
  projectId?: string | null;
  // FIXME: the balance field may not exist?
  balance?: number | null;
  check?: {
    number?: string | null;
    memo?: string | null;
  } | null;
  // FIXME: is coordinates needed? what does the field look like?
  coordinates?: any | null;
  invoiceNo?: string | null;
  toEmailAddresses?: string[] | null;
  ccEmailAddresses?: string[] | null;
  bccEmailAddresses?: string[] | null;

  sentOn?: string | null;

  billingAddress?: string | null;
  termsId?: string | null;
  dueOn?: string | null;

  locOfSale?: string | null;

  shippingAddress?: string | null;
  shippingVia?: string | null;
  shippingOn?: string | null;
  shippingTrackingNo?: string | null;

  msgOnInvoice?: string | null;
  msgOnStatement?: string | null;

  emailSubject?: string | null;
  emailMessage?: string | null;

  paymentMethods?: List<PaymentMethodType> | Array<PaymentMethodType> | null;
};

export type TransactionRecord = RecordOf<TransactionProps>;

export const Transaction = (defaultValues: Partial<TransactionProps>) =>
  class extends Record<TransactionProps>({
    id: null,

    accountId: undefined,

    clientId: undefined, // this is a ONE TIME instantiation, don't do this.  uuidv4().toUpperCase(),
    postedOn: DateTime.local().toISODate()!,

    payee: null,
    memo: '',
    knownCategoryId: undefined,
    coa: chartOfAccountsTypes.mkChartOfAccount({
      id: '0',
      type: 'UNCATEGORIZED',
    }),
    amount: 0.0,
    split: null,

    businessId: undefined,
    customerId: undefined,
    projectId: undefined,

    tags: null,
    attachments: null,

    state: 'PENDING',
    matchState: 'NOT_MATCHED', // NOT_MATCHED, AUTO_MATCHED, USER_MATCHED, USER_UNMATCHED
    isReviewed: false,
    transfer: null,

    cpData: null,

    stModelId: null,
    stDueOn: null,
    paymentProvider: null,

    paymentSentStatus: null,
    paymentId: null,

    ebillId: null,

    source: TransactionSource.UserEntered,
    isDeleted: false,
    modifiedAt: undefined,

    isExcludedFromReports: false,
    isExcludedFromF2S: false,

    userFlag: null,
    userFlagNote: null,

    matchedTxn: null,

    isBill: false,
    isSubscription: false,

    isBillable: false,

    subtype: undefined,
    subtypeRelatedTxn: undefined,
    linkedItem: undefined,
    createdAt: undefined,

    allocations: undefined,
    // fields specific to web app (unknown to QCS)
    //
    balance: undefined,

    ...defaultValues,
  }) {};

export const TransactionPatch = (defaultValues: Partial<TransactionProps>) =>
  class extends Record<TransactionProps>({
    id: undefined,

    accountId: undefined,

    clientId: undefined,
    postedOn: DateTime.local().toISODate()!, // TODO: should be optional for patch mode

    payee: undefined,
    memo: undefined,
    coa: undefined,
    amount: 0.0, // TODO: should be optional for patch mode
    split: undefined,

    businessId: undefined,
    customerId: undefined,
    projectId: undefined,

    tags: undefined,
    attachments: undefined,

    state: undefined,
    matchState: undefined,
    isReviewed: undefined,
    transfer: undefined,

    cpData: undefined,

    stModelId: undefined,
    stDueOn: undefined,
    paymentProvider: undefined,

    paymentSentStatus: undefined,
    paymentId: undefined,

    ebillId: undefined,

    source: undefined,
    isDeleted: undefined,
    modifiedAt: undefined,

    isExcludedFromReports: undefined,
    isExcludedFromF2S: undefined,

    userFlag: undefined,
    userFlagNote: undefined,

    matchedTxn: undefined,

    isBill: undefined,
    isSubscription: undefined,

    isBillable: undefined,

    subtype: undefined,
    subtypeRelatedTxn: undefined,
    linkedItem: undefined,
    createdAt: undefined,

    allocations: undefined,
    // fields specific to web app (unknown to QCS)
    //
    balance: undefined,

    ...defaultValues,
  }) {};

// used to ensure tags, splits, and coa are saved as immutable records
function baseTxnMap(txn: Partial<TransactionProps> | RecordOf<TransactionProps> = {}) {
  return {
    ...('toJS' in txn ? txn.toJS() : txn),
    memo: txn.memo || undefined,
    coa: txn.coa ? chartOfAccountsTypes.mkChartOfAccount({ id: txn.coa.id, type: txn.coa.type }) : null,
    tags: txn.tags ? ImmutableList(txn.tags.map((tag) => tagsTypes.mkTag(tag))) : undefined,
    split: txn.split ? mkSplit(txn.split) : undefined,
    allocations: txn.allocations
      ? ImmutableList(txn.allocations.map((allocation) => mkAllocation(allocation)))
      : undefined,
    linkedItem: txn.linkedItem ? mkLinkedItem(txn.linkedItem) : undefined,
  } as Partial<TransactionProps>;
}

export class CashFlowTransaction extends Transaction({
  type: 'CASH_FLOW',
  check: {
    number: '',
    memo: '',
  },
  coordinates: null,
}) {}

export const mkCashFlowTransaction = (txn: Partial<TransactionProps> | RecordOf<TransactionProps> = {}) =>
  new CashFlowTransaction({
    ...baseTxnMap(txn),
    check: txn.check,
    coordinates: txn.coordinates,
  });

export class CashFlowTransactionPatch extends TransactionPatch({
  type: 'CASH_FLOW',
  check: undefined,
  coordinates: undefined,
}) {}

export const mkTransactionPatch = (txn: Partial<TransactionProps> | RecordOf<TransactionProps> = {}) =>
  new CashFlowTransactionPatch({
    ...baseTxnMap(txn),
    check: txn.check,
    coordinates: txn.coordinates,
  });

export class InvoiceTransaction extends Transaction({
  type: 'INVOICE',

  invoiceNo: undefined,

  toEmailAddresses: undefined,
  ccEmailAddresses: undefined,
  bccEmailAddresses: undefined,

  sentOn: undefined,

  billingAddress: undefined,
  termsId: undefined,
  dueOn: undefined,

  locOfSale: undefined,

  shippingAddress: undefined,
  shippingVia: undefined,
  shippingOn: undefined,
  shippingTrackingNo: undefined,

  msgOnInvoice: undefined,
  msgOnStatement: undefined,

  emailSubject: undefined,
  emailMessage: undefined,

  paymentMethods: undefined,
}) {}

export const mkInvoiceTransaction = (txn: Partial<TransactionProps> | RecordOf<TransactionProps> = {}) =>
  new InvoiceTransaction({
    ...baseTxnMap(txn),

    invoiceNo: txn.invoiceNo,

    toEmailAddresses: txn.toEmailAddresses,
    ccEmailAddresses: txn.ccEmailAddresses,
    bccEmailAddresses: txn.bccEmailAddresses,

    sentOn: txn.sentOn,

    billingAddress: txn.billingAddress,
    termsId: txn.termsId,
    dueOn: txn.dueOn,

    locOfSale: txn.locOfSale,

    shippingAddress: txn.shippingAddress,
    shippingVia: txn.shippingVia,
    shippingOn: txn.shippingOn,
    shippingTrackingNo: txn.shippingTrackingNo,

    msgOnInvoice: txn.msgOnInvoice,
    msgOnStatement: txn.msgOnStatement,

    emailSubject: txn.emailSubject,
    emailMessage: txn.emailMessage,

    paymentMethods: txn.paymentMethods ? ImmutableList(txn.paymentMethods) : undefined,
  });

export const InvestmentTxnType = Object.freeze({
  ADD_SHARES: 'ADD_SHARES', // aggregated
  BUY: 'BUY', // aggregated
  BUY_BONDS: 'BUY_BONDS',
  COVER_SHORT_SELL: 'COVER_SHORT_SELL',
  EXPENSE_MARGIN_INTEREST: 'EXPENSE_MARGIN_INTEREST',
  EXPENSE_MISCELLANEOUS: 'EXPENSE_MISCELLANEOUS', // aggregated
  CAPITAL_GAIN_LONG: 'CAPITAL_GAIN_LONG',
  CAPITAL_GAIN_SHORT: 'CAPITAL_GAIN_SHORT',
  INCOME_DIVIDEND: 'INCOME_DIVIDEND', // aggregated
  INCOME_INTEREST: 'INCOME_INTEREST', // aggregated
  INCOME_MISCELLANEOUS: 'INCOME_MISCELLANEOUS', // aggregated
  REINVEST_CAPITAL_GAIN_LONG: 'REINVEST_CAPITAL_GAIN_LONG',
  REINVEST_CAPITAL_GAIN_SHORT: 'REINVEST_CAPITAL_GAIN_SHORT',
  REINVEST_DIVIDEND: 'REINVEST_DIVIDEND', // aggregated
  REINVEST_INTEREST: 'REINVEST_INTEREST',
  REMOVE_SHARES: 'REMOVE_SHARES', // aggregated
  RETURN_OF_CAPITAL: 'RETURN_OF_CAPITAL',
  SELL: 'SELL', // aggregated
  SELL_BONDS: 'SELL_BONDS',
  SHORT_SELL: 'SHORT_SELL', // aggregated
  STOCK_DIVIDEND: 'STOCK_DIVIDEND',
  STOCK_SPLIT: 'STOCK_SPLIT',
  UNKNOWN: 'UNKNOWN',
});

export const SecurityType = Object.freeze({
  STOCK: 'STOCK',
  MUTUALFUND: 'MUTUALFUND',
  DEBT: 'DEBT',
  OPTION: 'OPTION',
});

export class InvestmentTransaction extends Transaction({
  type: 'INVESTMENT',
  investmentTxnType: '', // InvestmentTxnType
  units: 0,
  symbol: '', // deprecated - use localSecurityId instead
  securityName: '', // deprecated - use localSecurityId instead
  securityId: undefined, // deprecated - use localSecurityId instead
  securityIdType: undefined, // deprecated - use localSecurityId instead
  securityType: undefined, // deprecated - use localSecurityId instead
  localSecurityId: undefined,
  costBasis: 0,
  commission: 0,
  splitNumerator: undefined,
  splitDenominator: undefined,

  // props below are not confirmed
  acquisitionOn: undefined,
  currency: undefined,
}) {}

export const mkInvestmentTransaction = (txn = {}) =>
  new InvestmentTransaction({
    ...baseTxnMap(txn),
  });

export const statusOrder = ['RECONCILED', 'CLEARED', 'PENDING'];

export const calTxnLabelTypes = {
  income: 'income',
  expense: 'expense',
  overdue: 'overdue',
  normal: 'normal',
  more: 'more',
} as const;

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [TRANSACTIONS_REDUCER_KEY]: TransactionStore;
  }
}

export interface TransactionItemCombinedProps extends Omit<TransactionProps, 'type'>, Omit<SplitItemProps, 'type'> {
  type?: TransactionType | (typeof SplitItemType)[keyof typeof SplitItemType] | null;
}

// TransactionItem is a composition of 'transaction' + 'splitItem' ('item' is a mix of both for convenience - keep in mind that some props might collide)
//
export type TransactionItemProps = {
  transaction: RecordOf<TransactionProps>; // parent transction
  splitItem?: RecordOf<SplitItemProps>; // optional split item
  item: TransactionItemCombinedProps; // for convenience
};
export type TransactionItem = RecordOf<TransactionItemProps>;
const TransactionItemRecord = Record<TransactionItemProps>({
  transaction: undefined!, // Using `!` to bypass undefined, will be set by factory
  splitItem: undefined,
  item: undefined!, // Using `!` to bypass undefined, will be set by factory
});
export const mkTransactionItem = (props: {
  transaction: RecordOf<TransactionProps>;
  splitItem?: RecordOf<SplitItemProps>;
  item: TransactionItemCombinedProps;
}): TransactionItem => {
  return TransactionItemRecord(props) as TransactionItem;
};

export type A2ATransferPayload = {
  srcAccountId: string;
  dstAccountId: string;
  amount: string;
  initiateOn?: string; // if omited defaults to current day I think
};
